/**
 * Offline Sync Hook
 * 
 * Periodically checks for pending mutations and syncs them when online.
 * Runs every 30 seconds and only when authenticated and online.
 */

import { useEffect, useState, useRef } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useIdleDetection } from '@/hooks/useIdleDetection';
import { useAuth } from '@/lib/auth-store';
import { 
  getPendingMutations, 
  markSynced, 
  removeMutation, 
  incrementRetryCount,
  type QueuedMutation,
  getPendingRetryUploads,
  removeRetryUpload,
} from '@/lib/offlineQueue';
import { apiFetch } from '@/lib/api';
import API_BASE from '@/lib/apiBase';
import { apiRequest } from '@/lib/queryClient';

const SYNC_INTERVAL_ACTIVE_MS = 30000; // 30 seconds when mutations pending
const SYNC_INTERVAL_IDLE_MS = 300000; // 5 minutes when idle (no pending mutations)
const RETRY_INTERVAL_MS = 60000; // 60 seconds for retry uploads
const MAX_RETRIES = 3;

interface SyncState {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
}

export function useOfflineSync() {
  const isOnline = useNetworkStatus();
  const isIdle = useIdleDetection(300000); // 5 minutes
  const { state } = useAuth();
  const isAuthenticated = state.status === 'authenticated';
  
  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: null,
  });

  const syncIntervalRef = useRef<number | null>(null);
  const isSyncingRef = useRef(false);

  /**
   * Sync a single mutation to the backend
   */
  const syncMutation = async (mutation: QueuedMutation): Promise<boolean> => {
    try {
      console.log('[OfflineSync] Syncing mutation:', mutation.id, mutation.type, mutation.url);

      // Build the full URL
      const url = mutation.url.startsWith('http') 
        ? mutation.url 
        : `${API_BASE}${mutation.url.startsWith('/') ? mutation.url : `/${mutation.url}`}`;

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(mutation.headers || {}),
      };

      // Get auth token if available
      if (isAuthenticated) {
        try {
          const { getAccessToken } = await import('@/lib/authBootstrap');
          const token = await getAccessToken();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn('[OfflineSync] Failed to get auth token:', error);
        }
      }

      // Determine if payload is FormData (for file uploads)
      let body: BodyInit;
      if (mutation.isFormData) {
        // Reconstruct FormData from stored format
        const { reconstructPayload } = await import('@/lib/offlineQueue');
        body = reconstructPayload(mutation) as FormData;
        // Remove Content-Type header for FormData (browser sets it automatically)
        delete headers['Content-Type'];
      } else {
        body = JSON.stringify(mutation.payload);
      }

      // Make the request
      const response = await fetch(url, {
        method: mutation.type,
        headers,
        credentials: 'omit',
        body,
      });

      // Handle response
      if (response.ok) {
        console.log('[OfflineSync] Mutation synced successfully:', mutation.id);
        await markSynced(mutation.id);
        await removeMutation(mutation.id);
        return true;
      } else {
        const status = response.status;
        
        // For 409 (conflict) or 400 (bad request), mark as synced to avoid infinite loop
        // These errors typically indicate the mutation was already processed or is invalid
        if (status === 409 || status === 400) {
          console.warn('[OfflineSync] Mutation rejected by server (409/400), marking as synced:', mutation.id);
          await markSynced(mutation.id);
          await removeMutation(mutation.id);
          return true; // Consider it "synced" to avoid retrying
        }

        // For other errors, increment retry count
        await incrementRetryCount(mutation.id);
        const retryCount = (mutation.retryCount || 0) + 1;
        
        if (retryCount >= MAX_RETRIES) {
          console.error('[OfflineSync] Mutation failed after max retries, marking as synced:', mutation.id);
          await markSynced(mutation.id);
          await removeMutation(mutation.id);
          return true; // Give up and mark as synced
        }

        console.warn('[OfflineSync] Mutation sync failed, will retry:', mutation.id, status);
        return false;
      }
    } catch (error) {
      console.error('[OfflineSync] Error syncing mutation:', mutation.id, error);
      
      // Increment retry count
      await incrementRetryCount(mutation.id);
      const retryCount = (mutation.retryCount || 0) + 1;
      
      if (retryCount >= MAX_RETRIES) {
        console.error('[OfflineSync] Mutation failed after max retries, marking as synced:', mutation.id);
        await markSynced(mutation.id);
        await removeMutation(mutation.id);
        return true; // Give up and mark as synced
      }

      return false;
    }
  };

  /**
   * Sync all pending mutations sequentially
   */
  const syncAllPending = async () => {
    // Prevent concurrent syncs
    if (isSyncingRef.current) {
      console.log('[OfflineSync] Sync already in progress, skipping');
      return;
    }

    if (!isOnline || !isAuthenticated) {
      return;
    }

    try {
      isSyncingRef.current = true;
      setSyncState(prev => ({ ...prev, isSyncing: true }));

      const pending = await getPendingMutations();
      setSyncState(prev => ({ ...prev, pendingCount: pending.length }));

      if (pending.length === 0) {
        setSyncState(prev => ({ ...prev, isSyncing: false }));
        return;
      }

      console.log('[OfflineSync] Starting sync of', pending.length, 'pending mutations');

      // Sync mutations sequentially to avoid overwhelming the server
      let successCount = 0;
      for (const mutation of pending) {
        const success = await syncMutation(mutation);
        if (success) {
          successCount++;
        }
        
        // Update pending count after each sync
        const remaining = await getPendingMutations();
        setSyncState(prev => ({ ...prev, pendingCount: remaining.length }));
      }

      console.log('[OfflineSync] Sync complete:', successCount, 'successful,', pending.length - successCount, 'failed');
      setSyncState(prev => ({ ...prev, lastSyncTime: Date.now() }));
    } catch (error) {
      console.error('[OfflineSync] Error during sync:', error);
    } finally {
      isSyncingRef.current = false;
      setSyncState(prev => ({ ...prev, isSyncing: false }));
    }
  };

  /**
   * Update pending count when mutations change
   */
  useEffect(() => {
    const updatePendingCount = async () => {
      const pending = await getPendingMutations();
      setSyncState(prev => ({ ...prev, pendingCount: pending.length }));
    };

    updatePendingCount();
    
    // Update count periodically
    const countInterval = setInterval(updatePendingCount, 5000);
    return () => clearInterval(countInterval);
  }, []);

  /**
   * Retry failed uploads from localStorage
   */
  const retryFailedUploads = async () => {
    if (!isOnline || !isAuthenticated) {
      return;
    }

    try {
      const pendingRetries = getPendingRetryUploads();
      if (pendingRetries.length === 0) {
        return;
      }

      console.log('[OfflineSync] Retrying', pendingRetries.length, 'failed uploads');

      for (let i = pendingRetries.length - 1; i >= 0; i--) {
        const retryItem = pendingRetries[i];
        const retryCount = retryItem.retryCount || 0;

        if (retryCount >= MAX_RETRIES) {
          console.warn('[OfflineSync] Retry upload exceeded max retries, removing:', retryItem);
          removeRetryUpload(i);
          continue;
        }

        try {
          const { logEvent } = await import('@/lib/logger');
          logEvent('evidence_submission_retry_attempt', {
            assignmentId: retryItem.storeId,
            retryCount: retryCount + 1,
            queuedAt: retryItem.queuedAt,
          });

          const response = await apiRequest('/api/mobile/mobile-submission', 'POST', {
            storeId: retryItem.storeId,
            operatorId: retryItem.operatorId,
            campaignId: retryItem.campaignId,
            cycleId: retryItem.cycleId,
            evidenceAssets: retryItem.evidenceAssets || [],
            signatureUrl: retryItem.signatureUrl,
            notes: retryItem.notes,
          });

          if (response.ok) {
            const { logEvent: logSuccess } = await import('@/lib/logger');
            logSuccess('evidence_submission_retry_success', {
              assignmentId: retryItem.storeId,
              retryCount: retryCount + 1,
            });

            console.log('[OfflineSync] Retry upload successful:', retryItem);
            removeRetryUpload(i);

            // Show success toast
            const { useToast } = await import('@/hooks/use-toast');
            // Note: We can't use hooks here, so we'll just log
            console.log('[OfflineSync] Retry successful - upload completed');
          } else {
            // Increment retry count
            retryItem.retryCount = retryCount + 1;
            const updated = [...pendingRetries];
            updated[i] = retryItem;
            localStorage.setItem('pendingUploads', JSON.stringify(updated));

            const { logEvent: logFail } = await import('@/lib/logger');
            logFail('evidence_submission_retry_failed', {
              assignmentId: retryItem.storeId,
              retryCount: retryCount + 1,
              status: response.status,
            });

            console.warn('[OfflineSync] Retry upload failed:', response.status);
          }
        } catch (error) {
          // Increment retry count
          retryItem.retryCount = retryCount + 1;
          const updated = [...pendingRetries];
          updated[i] = retryItem;
          localStorage.setItem('pendingUploads', JSON.stringify(updated));

          const { logEvent: logError } = await import('@/lib/logger');
          logError('evidence_submission_retry_error', {
            assignmentId: retryItem.storeId,
            retryCount: retryCount + 1,
            error: error instanceof Error ? error.message : String(error),
          });

          console.error('[OfflineSync] Retry upload error:', error);
        }
      }
    } catch (error) {
      console.error('[OfflineSync] Error during retry:', error);
    }
  };

  /**
   * Set up periodic sync when online and authenticated
   * Uses adaptive interval: 30s when mutations pending, 5min when idle
   * Pauses completely when user is idle (no activity for 5+ minutes)
   */
  useEffect(() => {
    if (!isOnline || !isAuthenticated) {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      return;
    }

    // Pause sync when user is idle
    if (isIdle) {
      console.log('[OfflineSync] User idle - pausing background sync');
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      return;
    }

    // Sync immediately when coming online or resuming from idle
    syncAllPending();
    retryFailedUploads();

    // Determine sync interval based on pending count
    const hasPending = syncState.pendingCount > 0;
    const intervalMs = hasPending ? SYNC_INTERVAL_ACTIVE_MS : SYNC_INTERVAL_IDLE_MS;
    
    console.log(`[OfflineSync] Setting sync interval to ${intervalMs}ms (${hasPending ? 'active' : 'idle'} mode, ${syncState.pendingCount} pending)`);

    // Then sync periodically
    syncIntervalRef.current = window.setInterval(() => {
      syncAllPending();
    }, intervalMs);

    // Retry failed uploads on a longer interval
    const retryInterval = window.setInterval(() => {
      retryFailedUploads();
    }, RETRY_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, [isOnline, isAuthenticated, isIdle, syncState.pendingCount]);

  /**
   * Also sync when coming back online (online event)
   */
  useEffect(() => {
    if (!isOnline) return;

    const handleOnline = () => {
      console.log('[OfflineSync] Online event detected, syncing pending mutations and retries');
      syncAllPending();
      retryFailedUploads();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isOnline]);

  return syncState;
}

